import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productsAPI, companyAPI } from '../../services/api';
import { useCompany } from '../common/useCompany';
import toast from 'react-hot-toast';
import '../../styles/data-form.css';
import { getDisplayCurrency, CURRENCY_REGISTRY, convertAmount, makeMoneyFormatter } from '../../utils/currency';

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = useCompany();
  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState(CURRENCY_REGISTRY);
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({
    product_type: 'NonInventory', name: '', sku: '', description: '',
    purchase_cost: 0, sales_price: 0, tax_rate: 0,
    is_tracked: false, reorder_point: 0, category_id: null,
    currency: baseCurrency,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    productsAPI.categories().then(setCategories).catch(() => {});
    companyAPI.currencies().then((cs) => { if (Array.isArray(cs) && cs.length) setCurrencies(cs); }).catch(() => {});
    companyAPI.exchangeRates().then((rs) => setRates(Array.isArray(rs) ? rs : [])).catch(() => {});
    if (id) productsAPI.get(id).then(d => setForm(prev => ({ ...prev, ...d, currency: d.currency || prev.currency }))).catch(e => toast.error(e.message));
  }, [id]);

  const rateFor = useMemo(() => {
    const c = form.currency || baseCurrency;
    if (!c || c === baseCurrency) return 1;
    const direct = rates.find(r => r.currency === c && r.to_currency === baseCurrency);
    if (direct && parseFloat(direct.rate)) return parseFloat(direct.rate);
    const reverse = rates.find(r => r.currency === baseCurrency && r.to_currency === c);
    if (reverse && parseFloat(reverse.rate)) return 1 / parseFloat(reverse.rate);
    return 1;
  }, [form.currency, baseCurrency, rates]);

  const fmtBase = makeMoneyFormatter(baseCurrency);
  const costBase = form.purchase_cost && rateFor ? convertAmount(form.purchase_cost, rateFor) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (id) { await productsAPI.update(id, form); toast.success('Product updated'); }
      else { await productsAPI.create(form); toast.success('Product created'); }
      navigate('/products');
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete product "${form.name}"? This cannot be undone.`)) return;
    try {
      await productsAPI.remove(id);
      toast.success('Product deleted');
      navigate('/products');
    } catch (err) { toast.error(err.message); }
  };

  const u = (f) => (e) => setForm({ ...form, [f]: e.target.type === 'checkbox' ? e.target.checked : (e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value) });

  return (
    <div className="df-editor">
      <div className="df-section-header"><span>{id ? 'Edit' : 'New'} Product / Service</span></div>
      <form onSubmit={handleSubmit}>
        <div className="df-body">
          <div className="df-section-title">General</div>
          <div className="df-grid">
            <div className="df-field">
              <label>Type</label>
              <select value={form.product_type} onChange={u('product_type')}>
                <option value="Service">Service</option>
                <option value="Inventory">Inventory</option>
                <option value="NonInventory">Non-Inventory</option>
              </select>
            </div>
            <div className="df-field"><label>Name <span className="req">*</span></label><input required value={form.name} onChange={u('name')} /></div>
            <div className="df-field"><label>SKU</label><input value={form.sku || ''} onChange={u('sku')} /></div>
            <div className="df-field">
              <label>Category</label>
              <select value={form.category_id || ''} onChange={e => setForm({ ...form, category_id: parseInt(e.target.value) || null })}>
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="df-divider" />
          <div className="df-section-title">Pricing</div>
          <div className="df-grid cols-4">
            <div className="df-field"><label>Sales Price</label><input type="number" step="0.01" min="0" value={form.sales_price || 0} onChange={u('sales_price')} /></div>
            <div className="df-field"><label>Purchase Cost</label><input type="number" step="0.01" min="0" value={form.purchase_cost || 0} onChange={u('purchase_cost')} />
              {form.currency && form.currency !== baseCurrency && form.purchase_cost > 0 && rateFor !== 1 && (
                <small className="fx-hint">≈ {fmtBase(costBase)} in {baseCurrency}</small>
              )}
            </div>
            <div className="df-field"><label>Purchase Currency</label>
              <select value={form.currency || baseCurrency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </select>
            </div>
            <div className="df-field"><label>Tax Rate (%)</label><input type="number" step="0.01" min="0" value={form.tax_rate || 0} onChange={u('tax_rate')} /></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div className="df-check-row">
              <input type="checkbox" id="tracked" checked={form.is_tracked || false} onChange={u('is_tracked')} />
              <label htmlFor="tracked">Inventory Tracked</label>
            </div>
          </div>
          {form.is_tracked && (
            <div className="df-grid" style={{ marginTop: 8 }}>
              <div className="df-field"><label>Reorder Point</label><input type="number" step="1" min="0" value={form.reorder_point || 0} onChange={u('reorder_point')} /></div>
            </div>
          )}
          <div className="df-divider" />
          <div className="df-section-title">Description</div>
          <div className="df-grid"><div className="df-field full"><textarea rows={3} value={form.description || ''} onChange={u('description')} placeholder="Product description..." /></div></div>
        </div>
        <div className="df-actions">
          <div>
            {id && <button type="button" className="inv-btn inv-btn-danger" onClick={handleDelete}>Delete</button>}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button type="button" className="inv-btn inv-btn-secondary" onClick={() => navigate('/products')}>Cancel</button>
            <button type="submit" className="inv-btn inv-btn-primary" disabled={loading}>{loading ? 'Saving...' : id ? 'Update' : 'Create'} Product</button>
          </div>
        </div>
      </form>
    </div>
  );
}
